import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Loader2, Pencil, Trash2, Eye, FileText, CheckCircle2, Send, XCircle, Clock, Wallet, TrendingUp, Calendar, MapPin, ReceiptText, ExternalLink } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { formatIDR, formatIDRCompact, formatDateID } from '../lib/format';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { QuotationFormDialog } from '../components/QuotationFormDialog';
import { AdvancedFilterBar } from '../components/AdvancedFilterBar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';

const STATUS_TABS = [
  { value: 'all', label: 'Semua' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Sent', label: 'Terkirim' },
  { value: 'Approved', label: 'Disetujui' },
  { value: 'Rejected', label: 'Ditolak' },
  { value: 'Expired', label: 'Kadaluarsa' },
];

// Compact summary tile (used in horizontal-scroll strip on mobile, grid on desktop)
const SummaryTile = ({ label, value, accent = 'default', icon: Icon, testid }) => {
  const tones = {
    default: 'bg-card border-border text-foreground',
    primary: 'bg-primary/10 border-primary/30 text-primary',
    success: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300',
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-300',
    danger: 'bg-red-500/10 border-red-500/30 text-red-300',
    warn: 'bg-orange-500/10 border-orange-500/30 text-orange-300',
    muted: 'bg-white/5 border-white/10 text-muted-foreground',
  };
  return (
    <Card data-testid={testid} className={`shrink-0 min-w-[150px] sm:min-w-0 ${tones[accent]} border`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[11px] sm:text-xs font-medium opacity-80 uppercase tracking-wide">{label}</div>
          {Icon ? <Icon className="h-4 w-4 opacity-70" /> : null}
        </div>
        <div className="mt-1.5 text-lg sm:text-xl font-semibold font-heading tracking-tight font-mono-num">
          {value}
        </div>
      </CardContent>
    </Card>
  );
};

export default function Quotations() {
  const { role } = useAuth();
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [allList, setAllList] = useState([]); // unfiltered for KPIs
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    venue: '',
    event_date_from: '',
    event_date_to: '',
  });
  const [statusTab, setStatusTab] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      // Status tab takes precedence over advanced status
      if (statusTab !== 'all') params.status = statusTab;
      else if (filters.status) params.status = filters.status;
      if (filters.venue) params.venue = filters.venue;
      if (filters.event_date_from) params.event_date_from = filters.event_date_from;
      if (filters.event_date_to) params.event_date_to = filters.event_date_to;
      const { data } = await api.get('/quotations', { params });
      setList(data);
    } finally { setLoading(false); }
  };

  // Fetch full list (no filters) for KPI summary, in parallel
  const loadAll = async () => {
    try {
      const { data } = await api.get('/quotations');
      setAllList(data);
    } catch (err) {
      // KPI summary is best-effort. We still log so backend or auth issues
      // get surfaced rather than silently masked.
      console.error('[Quotations] failed to load KPI summary list', err);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [filters, statusTab]);

  useEffect(() => { loadAll(); }, []);

  const kpis = useMemo(() => {
    const counts = { Draft: 0, Sent: 0, Approved: 0, Rejected: 0, Expired: 0 };
    let approvedValue = 0;
    allList.forEach(q => {
      if (counts[q.status] !== undefined) counts[q.status] += 1;
      if (q.status === 'Approved') approvedValue += Number(q.total_amount) || 0;
    });
    const total = allList.length;
    const conv = total > 0 ? Math.round((counts.Approved / total) * 100) : 0;
    return { total, ...counts, approvedValue, conv };
  }, [allList]);

  const openNew = () => { setEditing(null); setDialogOpen(true); };
  const openEdit = (q) => { setEditing(q); setDialogOpen(true); };

  const removeQ = async (q) => {
    try {
      await api.delete(`/quotations/${q.id}`);
      toast.success('Penawaran dihapus');
      load();
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menghapus');
    }
  };

  const [converting, setConverting] = useState(null); // quotation_id while converting
  const convertToInvoice = async (q) => {
    setConverting(q.id);
    try {
      const { data: inv } = await api.post(`/quotations/${q.id}/convert-to-invoice`);
      toast.success(`Invoice ${inv.invoice_number} berhasil dibuat`);
      navigate(`/invoices/${inv.id}`);
    } catch (e) {
      if (e?.response?.status === 409) {
        toast.error('Invoice untuk penawaran ini sudah pernah dibuat.');
        load();
        loadAll();
      } else {
        toast.error(e?.response?.data?.detail || 'Gagal membuat invoice');
      }
    } finally { setConverting(null); }
  };

  const onSaved = () => { load(); loadAll(); };

  const canEdit = PERMISSIONS.canEdit(role);
  const canDelete = PERMISSIONS.canDeleteFinancial(role);

  return (
    <div className="space-y-4" data-testid="quotations-page">
      {/* Page Title + primary CTA */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-semibold tracking-tight">Penawaran</h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">Kelola seluruh penawaran event Glowing Production</p>
        </div>
        {canEdit && (
          <Button onClick={openNew} className="h-10 sm:h-9" data-testid="quotations-add-button">
            <Plus className="h-4 w-4 mr-2" /> Buat Penawaran
          </Button>
        )}
      </div>

      {/* KPI Summary — horizontal scroll on mobile, responsive grid on tablet+ */}
      <div
        className="-mx-4 px-4 flex gap-3 overflow-x-auto pb-2 sm:mx-0 sm:px-0 sm:grid sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-4 sm:gap-3 sm:overflow-visible scroll-smooth"
        data-testid="quotations-kpi-strip"
      >
        <SummaryTile label="Total Penawaran" value={kpis.total} icon={FileText} testid="quotations-kpi-total" />
        <SummaryTile label="Draft" value={kpis.Draft} accent="muted" icon={Clock} testid="quotations-kpi-draft" />
        <SummaryTile label="Terkirim" value={kpis.Sent} accent="blue" icon={Send} testid="quotations-kpi-sent" />
        <SummaryTile label="Disetujui" value={kpis.Approved} accent="success" icon={CheckCircle2} testid="quotations-kpi-approved" />
        <SummaryTile label="Ditolak" value={kpis.Rejected} accent="danger" icon={XCircle} testid="quotations-kpi-rejected" />
        <SummaryTile label="Kadaluarsa" value={kpis.Expired} accent="warn" icon={Clock} testid="quotations-kpi-expired" />
        <SummaryTile label="Nilai Disetujui" value={formatIDRCompact(kpis.approvedValue)} accent="primary" icon={Wallet} testid="quotations-kpi-approved-value" />
        <SummaryTile label="Conversion Rate" value={`${kpis.conv}%`} accent="primary" icon={TrendingUp} testid="quotations-kpi-conv" />
      </div>

      {/* Search + Advanced Filter Bar + Quick Status Filter Pills */}
      <div className="space-y-3">
        <AdvancedFilterBar
          value={filters}
          onChange={(patch) => setFilters(prev => ({ ...prev, ...patch }))}
          onClear={() => { setFilters({ search: '', status: '', venue: '', event_date_from: '', event_date_to: '' }); setStatusTab('all'); }}
          searchPlaceholder="Cari nomor penawaran, client, venue, atau deskripsi event"
          testidPrefix="quotations-filter"
        />
        <div
          className="-mx-4 px-4 flex gap-2 overflow-x-auto sm:mx-0 sm:px-0 sm:flex-wrap pb-1"
          data-testid="quotations-filter-pills"
          role="tablist"
        >
          {STATUS_TABS.map((t) => {
            const active = statusTab === t.value;
            return (
              <button
                type="button"
                role="tab"
                key={t.value}
                onClick={() => setStatusTab(t.value)}
                data-testid={`quotations-filter-${t.value}`}
                className={`shrink-0 inline-flex items-center h-9 px-3.5 rounded-full text-sm font-medium border transition-colors ${
                  active
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-card text-muted-foreground border-border hover:text-foreground hover:bg-white/5'
                }`}
              >
                {t.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* DESKTOP Table */}
      <Card className="hidden md:block bg-card border-border">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table data-testid="quotations-table">
              <TableHeader>
                <TableRow className="border-border hover:bg-transparent">
                  <TableHead>No</TableHead>
                  <TableHead>Tanggal</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Event</TableHead>
                  <TableHead>Venue</TableHead>
                  <TableHead className="text-right">Total</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Berlaku</TableHead>
                  <TableHead className="text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading && (
                  <TableRow><TableCell colSpan={9} className="text-center py-10 text-muted-foreground">
                    <Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…
                  </TableCell></TableRow>
                )}
                {!loading && list.length === 0 && (
                  <TableRow><TableCell colSpan={9} className="p-0">
                    <EmptyState
                      icon={FileText}
                      title="Belum ada penawaran"
                      description="Mulai buat penawaran pertama untuk client Glowing Production."
                      action={canEdit ? <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Buat Penawaran</Button> : null}
                    />
                  </TableCell></TableRow>
                )}
                {!loading && list.map(q => (
                  <TableRow key={q.id} className="hover:bg-white/4 border-border" data-testid={`quotations-row-${q.id}`}>
                    <TableCell className="font-mono-num text-xs">
                      <div>{q.quotation_number}</div>
                      {q.linked_invoice ? (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${q.linked_invoice.id}`); }}
                          className="mt-0.5 inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                          data-testid={`quotations-row-linked-${q.id}`}
                        >
                          → {q.linked_invoice.invoice_number}
                        </button>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{formatDateID(q.quotation_date)}</TableCell>
                    <TableCell className="font-medium">{q.client_name}</TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-[200px]">{q.event_description}</TableCell>
                    <TableCell className="text-muted-foreground truncate max-w-[160px]">{q.venue}</TableCell>
                    <TableCell className="text-right font-mono-num">{formatIDR(q.total_amount)}</TableCell>
                    <TableCell><StatusBadge status={q.status} /></TableCell>
                    <TableCell className="text-muted-foreground text-xs">{q.valid_until_date ? formatDateID(q.valid_until_date) : '—'}</TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {/* Convert / Lihat Invoice */}
                        {q.status === 'Approved' && !q.linked_invoice && canEdit && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8 text-primary hover:text-primary"
                                disabled={converting === q.id}
                                title="Convert ke Invoice"
                                data-testid={`quotations-convert-${q.id}`}
                              >
                                {converting === q.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <ReceiptText className="h-4 w-4" />}
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Buat invoice dari penawaran ini?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Data client, event, venue, item, dan total akan otomatis disalin ke invoice baru.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => convertToInvoice(q)} data-testid={`quotations-convert-confirm-${q.id}`}>Buat Invoice</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                        {q.linked_invoice && (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-8 w-8 text-primary hover:text-primary"
                            onClick={() => navigate(`/invoices/${q.linked_invoice.id}`)}
                            title="Lihat Invoice"
                            data-testid={`quotations-view-invoice-${q.id}`}
                          >
                            <ExternalLink className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => navigate(`/quotations/${q.id}`)} data-testid={`quotations-view-${q.id}`}><Eye className="h-4 w-4" /></Button>
                        {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(q)}><Pencil className="h-4 w-4" /></Button>}
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-red-300" data-testid={`quotations-delete-${q.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader><AlertDialogTitle>Hapus penawaran?</AlertDialogTitle><AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription></AlertDialogHeader>
                              <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => removeQ(q)}>Hapus</AlertDialogAction></AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* MOBILE Cards */}
      <div className="md:hidden space-y-3" data-testid="quotations-mobile-list">
        {loading && (
          <div className="text-center py-10 text-muted-foreground">
            <Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…
          </div>
        )}
        {!loading && list.length === 0 && (
          <Card className="bg-card border-border">
            <CardContent className="p-0">
              <EmptyState
                icon={FileText}
                title="Belum ada penawaran"
                description="Mulai buat penawaran pertama untuk client Glowing Production."
                action={canEdit ? <Button onClick={openNew}><Plus className="h-4 w-4 mr-2" />Buat Penawaran</Button> : null}
              />
            </CardContent>
          </Card>
        )}
        {!loading && list.map((q) => (
          <Card
            key={q.id}
            className="bg-card border-border active:scale-[0.99] transition-transform"
            data-testid={`quotations-card-${q.id}`}
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <button
                    type="button"
                    onClick={() => navigate(`/quotations/${q.id}`)}
                    className="font-mono-num text-xs text-primary hover:underline"
                  >
                    {q.quotation_number}
                  </button>
                  <div className="font-semibold mt-0.5 truncate">{q.client_name}</div>
                  {q.linked_invoice ? (
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); navigate(`/invoices/${q.linked_invoice.id}`); }}
                      className="mt-1 inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-mono-num"
                      data-testid={`quotations-card-linked-${q.id}`}
                    >
                      <ReceiptText className="h-3 w-3" />→ {q.linked_invoice.invoice_number}
                    </button>
                  ) : null}
                </div>
                <StatusBadge status={q.status} />
              </div>

              {q.event_description ? (
                <div className="text-sm text-foreground/90 line-clamp-2">{q.event_description}</div>
              ) : null}

              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Calendar className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{formatDateID(q.quotation_date)}</span>
                </div>
                {q.venue ? (
                  <div className="flex items-center gap-1.5 min-w-0">
                    <MapPin className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">{q.venue}</span>
                  </div>
                ) : null}
                {q.valid_until_date ? (
                  <div className="flex items-center gap-1.5 min-w-0 col-span-2">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    <span className="truncate">Berlaku hingga {formatDateID(q.valid_until_date)}</span>
                  </div>
                ) : null}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-border">
                <span className="text-xs text-muted-foreground">Total</span>
                <span className="font-mono-num font-semibold text-base">{formatIDR(q.total_amount)}</span>
              </div>

              <div className="flex items-center gap-2 pt-1">
                {/* Primary action: Convert / Lihat Invoice / Detail */}
                {q.status === 'Approved' && !q.linked_invoice && canEdit ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        className="flex-1 h-10"
                        disabled={converting === q.id}
                        data-testid={`quotations-card-convert-${q.id}`}
                      >
                        {converting === q.id
                          ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                          : <ReceiptText className="h-4 w-4 mr-1.5" />}
                        Convert ke Invoice
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Buat invoice dari penawaran ini?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Data client, event, venue, item, dan total akan otomatis disalin ke invoice baru.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => convertToInvoice(q)} data-testid={`quotations-card-convert-confirm-${q.id}`}>Buat Invoice</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : q.linked_invoice ? (
                  <Button
                    type="button"
                    className="flex-1 h-10"
                    onClick={() => navigate(`/invoices/${q.linked_invoice.id}`)}
                    data-testid={`quotations-card-view-invoice-${q.id}`}
                  >
                    <ExternalLink className="h-4 w-4 mr-1.5" />Lihat Invoice
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="secondary"
                    className="flex-1 h-10"
                    onClick={() => navigate(`/quotations/${q.id}`)}
                    data-testid={`quotations-card-view-${q.id}`}
                  >
                    <Eye className="h-4 w-4 mr-1.5" />Detail
                  </Button>
                )}

                {/* Secondary: open detail (when primary is Convert/Lihat) */}
                {(q.status === 'Approved' || q.linked_invoice) && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-10 w-10 shrink-0"
                    size="icon"
                    onClick={() => navigate(`/quotations/${q.id}`)}
                    aria-label="Lihat detail penawaran"
                    data-testid={`quotations-card-view-detail-${q.id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                )}

                {canEdit && (
                  <Button
                    type="button"
                    variant="secondary"
                    className="h-10 w-10 shrink-0"
                    size="icon"
                    onClick={() => openEdit(q)}
                    aria-label="Edit"
                    data-testid={`quotations-card-edit-${q.id}`}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                {canDelete && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        variant="secondary"
                        className="h-10 w-10 shrink-0 text-red-300 hover:text-red-200"
                        size="icon"
                        aria-label="Hapus"
                        data-testid={`quotations-card-delete-${q.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Hapus penawaran?</AlertDialogTitle>
                        <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction onClick={() => removeQ(q)}>Hapus</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <QuotationFormDialog open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={onSaved} />
    </div>
  );
}
